import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { MILESTONES } from "@/data/dholera";

const STATUS_STYLES = {
  Completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "Under Construction": "border-orange-500/40 bg-orange-500/10 text-orange-400",
  Upcoming: "border-slate-500/40 bg-slate-500/10 text-slate-400",
};

const BAR_STYLES = {
  Completed: "bg-emerald-400",
  "Under Construction": "bg-orange-400",
  Upcoming: "bg-slate-500",
};

const AGGREGATE = Math.round(
  MILESTONES.reduce((sum, m) => sum + m.progress, 0) / MILESTONES.length
);

export default function InfraTracker() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section
      id="infrastructure"
      className="relative bg-[#081124] text-slate-100"
      data-testid="infra-section"
    >
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400"
            >
              Sec. 03 — Build Status
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 font-display text-3xl font-medium uppercase tracking-tight text-white md:text-5xl"
            >
              Infrastructure Tracker
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400"
            >
              Land appreciates when concrete pours. Track every anchor project
              feeding the region — tap a milestone for scope and target dates.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-10 border border-white/10 p-6"
              data-testid="region-readiness"
            >
              <p className="font-display text-6xl font-extrabold tracking-tight text-emerald-400 md:text-7xl">
                {AGGREGATE}%
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                Aggregate region readiness
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-8">
            <div className="border-t border-white/10">
              {MILESTONES.map((m, i) => {
                const Icon = m.icon;
                const open = openIdx === i;
                return (
                  <motion.div
                    key={m.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.55, delay: i * 0.04 }}
                    className="border-b border-white/10"
                    data-testid={`infra-milestone-${i}`}
                  >
                    <button
                      onClick={() => setOpenIdx(open ? null : i)}
                      className="w-full py-5 text-left"
                      data-testid={`infra-toggle-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="hidden w-8 font-mono text-xs text-slate-500 sm:block">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/10 text-slate-300">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-display text-base font-medium text-white md:text-lg">
                            {m.name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-slate-400">
                            {m.note}
                          </span>
                        </span>
                        <span
                          className={`hidden shrink-0 border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] sm:block ${STATUS_STYLES[m.status]}`}
                          data-testid={`infra-status-${i}`}
                        >
                          {m.status}
                        </span>
                        <span className="w-12 shrink-0 text-right font-mono text-sm font-bold text-slate-200">
                          {m.progress}%
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                        />
                      </div>
                      <div className="mt-4 h-1 w-full bg-white/10">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${m.progress}%` }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                          className={`h-full ${BAR_STYLES[m.status]}`}
                          data-testid={`infra-progress-${i}`}
                        />
                      </div>
                    </button>
                    <AnimatePresence>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="overflow-hidden"
                          data-testid={`infra-detail-${i}`}
                        >
                          <div className="border-l-2 border-emerald-400/50 pb-6 pl-5 pr-2 sm:ml-12">
                            <p className="max-w-2xl text-sm leading-relaxed text-slate-300">
                              {m.detail}
                            </p>
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
                              Target — {m.target}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Progress figures are illustrative mock data
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
